export const SUPPORTED_LANGUAGES = ['es', 'en', 'ca', 'fr'] as const;
export type Language = typeof SUPPORTED_LANGUAGES[number];

export const DEFAULT_LANGUAGE: Language = 'es';

// Stores the language the user picked, so it is kept on the next visit
export const LANGUAGE_STORAGE_KEY = 'language_conectio_client';

export function isSupportedLanguage(lang: string | null | undefined): lang is Language {
  return !!lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

/** First supported language in the browser preferences (e.g. 'ca-ES' -> 'ca'), or the default. */
export function detectBrowserLanguage(): Language {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    const lang = tag?.toLowerCase().split('-')[0];
    if (isSupportedLanguage(lang)) {
      return lang;
    }
  }
  return DEFAULT_LANGUAGE;
}

const CONECTIO_SITE_HOSTS = ['conectio.io', 'www.conectio.io'];

/**
 * Language of the conectio.io page the visitor came from, which always uses /<lang>/ URLs
 * (e.g. https://conectio.io/fr/...). Read from the referrer, or from a `lang` query param
 * that conectio.io links can add, since browsers usually send only the referrer's origin.
 */
export function getConectioSiteLanguage(referrer: string, search: string): Language | null {
  try {
    const url = new URL(referrer);
    if (CONECTIO_SITE_HOSTS.includes(url.hostname)) {
      const lang = url.pathname.split('/')[1]?.toLowerCase();
      if (isSupportedLanguage(lang)) {
        return lang;
      }
    }
  } catch {
    // No referrer, or not a valid URL
  }
  const lang = new URLSearchParams(search).get('lang')?.toLowerCase();
  return isSupportedLanguage(lang) ? lang : null;
}

export function getStoredLanguage(): Language | null {
  try {
    const lang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupportedLanguage(lang) ? lang : null;
  } catch {
    return null;
  }
}

export function storeLanguage(lang: Language): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // Storage can be unavailable (private mode); the choice then lasts only this visit
  }
}

// Per-tab memory (sessionStorage), so the sign-up flow keeps its language after leaving for LinkedIn
// and coming back, when the referrer and ?lang= are gone:
// - SESSION_LANGUAGE_STORAGE_KEY: language of the conectio.io page this tab came from (top priority)
// - LAST_LANGUAGE_STORAGE_KEY: last language shown, used only instead of the browser language
export const SESSION_LANGUAGE_STORAGE_KEY = 'language_conectio_session';
export const LAST_LANGUAGE_STORAGE_KEY = 'language_conectio_last';

function readSessionLanguage(key: string): Language | null {
  try {
    const lang = sessionStorage.getItem(key);
    return isSupportedLanguage(lang) ? lang : null;
  } catch {
    return null;
  }
}

function writeSessionLanguage(key: string, lang: string): void {
  if (!isSupportedLanguage(lang)) {
    return;
  }
  try {
    sessionStorage.setItem(key, lang);
  } catch {
    // Storage can be unavailable (private mode); the detection rules then run again on each page load
  }
}

export const getSessionLanguage = () => readSessionLanguage(SESSION_LANGUAGE_STORAGE_KEY);
export const storeSessionLanguage = (lang: string) => writeSessionLanguage(SESSION_LANGUAGE_STORAGE_KEY, lang);
export const getLastLanguage = () => readSessionLanguage(LAST_LANGUAGE_STORAGE_KEY);
export const storeLastLanguage = (lang: string) => writeSessionLanguage(LAST_LANGUAGE_STORAGE_KEY, lang);
